import { useEffect, useState } from "react";
import { MapPin, Phone, Clock } from "lucide-react";
import PageHero from "../components/PageHero";
import SectionHeading from "../components/SectionHeading";
import CtaSection from "../components/CtaSection";
import { Reveal } from "../components/Reveal";
import { IMAGES, COMPANY, HISTORY } from "../data/site";
import { api } from "../lib/api";
import { useSiteImages } from "../lib/useSiteImages";

const OVERVIEW = [
  ["会社名", COMPANY.name],
  ["Web表記", "NEO CONSTRUCTION"],
  ["所在地", `${COMPANY.zip} ${COMPANY.address}`],
  ["代表者", `代表取締役 ${COMPANY.ceo}`],
  ["法人番号", COMPANY.corporateNumber],
  ["設立", COMPANY.founded],
  ["資本金", COMPANY.capital],
  ["従業員数", COMPANY.employees],
  ["事業内容", "土木工事 / 草刈り・環境整備 / 伐採工事 / 太陽光発電設備工事 / 不動産管理・開発事業"],
  ["建設業許可", `${COMPANY.license}（${COMPANY.licenseTypes}）`],
  ["インボイス登録番号", COMPANY.invoice],
  ["対応地域", COMPANY.area],
  ["営業時間", COMPANY.hours],
  ["定休日", COMPANY.holiday],
  ["TEL / FAX", `${COMPANY.tel} / ${COMPANY.fax}`],
  ["主要取引銀行", COMPANY.banks],
  ["保有資格", COMPANY.qualifications],
];

export default function Company() {
  const siteImages = useSiteImages();
  const [profile, setProfile] = useState(null);
  useEffect(() => {
    api.get("/company").then((r) => setProfile(r.data)).catch(() => {});
  }, []);
  const overviewRows = profile?.overview?.length ? profile.overview.map((r) => [r.label, r.value]) : OVERVIEW;
  const historyItems = profile?.history?.length ? profile.history : HISTORY;
  const contact = { zip: COMPANY.zip, address: COMPANY.address, tel: COMPANY.tel, fax: COMPANY.fax, hours: COMPANY.hours, holiday: COMPANY.holiday, ...(profile?.contact || {}) };
  return (
    <div data-testid="company-page">
      <PageHero eyebrow="COMPANY PROFILE" titleEn="COMPANY" titleJa="会社概要" desc="株式会社ネオ建設の会社情報・沿革・アクセスをご紹介します。" image={siteImages.company || IMAGES.facade} />

      <section className="border-t border-line py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="OVERVIEW" title="会社概要" />
          <Reveal delay={0.1}>
            <dl data-testid="company-overview-table" className="mt-14 border-t border-line">
              {overviewRows.map(([k, v], i) => (
                <div key={i} className="grid grid-cols-1 gap-1 border-b border-line py-5 sm:grid-cols-12 sm:gap-8">
                  <dt className="font-mono text-xs tracking-[0.2em] text-gray-500 sm:col-span-3 sm:pt-1">{k}</dt>
                  <dd className="text-sm text-gray-200 sm:col-span-9">{v}</dd>
                </div>
              ))}
            </dl>
          </Reveal>
          <p className="mt-6 font-mono text-[10px] tracking-[0.2em] text-gray-600">
            ※ 設立・資本金・許可番号・連絡先等は仮の情報です。正式情報確定後に更新されます。
          </p>
        </div>
      </section>

      <section className="border-t border-line bg-coal py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="HISTORY" title="沿革" />
          <div className="mt-14 max-w-3xl">
            {historyItems.map((h, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div data-testid={`history-item-${i}`} className="relative flex gap-8 border-l border-line pb-10 pl-8 last:pb-0">
                  <span className="absolute -left-[5px] top-1.5 h-2.5 w-2.5 rotate-45 bg-accent" />
                  <div className="w-24 shrink-0">
                    <p className="font-display text-2xl font-extrabold text-accent">{h.year}</p>
                    {h.label && <p className="mt-1 font-mono text-[9px] tracking-[0.25em] text-gray-500">{h.label}</p>}
                  </div>
                  <p className="pt-1 text-sm leading-relaxed text-gray-300">{h.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-line py-24 md:py-32">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <div>
            <SectionHeading eyebrow="ACCESS" title="アクセス" />
            <div className="mt-12 space-y-6">
              <Reveal>
                <div className="flex items-start gap-4">
                  <MapPin className="mt-1 h-5 w-5 shrink-0 text-accent" />
                  <div>
                    <p className="text-sm font-bold text-white">{COMPANY.name} 本社</p>
                    <p className="mt-1 text-sm text-gray-400">
                      {contact.zip}
                      <br />
                      {contact.address}
                    </p>
                  </div>
                </div>
              </Reveal>
              <Reveal delay={0.08}>
                <div className="flex items-start gap-4">
                  <Phone className="mt-1 h-5 w-5 shrink-0 text-accent" />
                  <p className="text-sm text-gray-400">
                    TEL {contact.tel} / FAX {contact.fax}
                  </p>
                </div>
              </Reveal>
              <Reveal delay={0.12}>
                <div className="flex items-start gap-4">
                  <Clock className="mt-1 h-5 w-5 shrink-0 text-accent" />
                  <p className="text-sm text-gray-400">
                    営業時間 {contact.hours} / 定休日 {contact.holiday}
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
          <Reveal delay={0.1}>
            <div className="relative overflow-hidden border border-line bg-coal">
              <iframe
                title="株式会社ネオ建設 アクセスマップ"
                data-testid="company-map"
                src={`https://www.google.com/maps?q=${encodeURIComponent(`${contact.zip} ${contact.address}`)}&z=15&output=embed`}
                className="h-full min-h-[340px] w-full border-0 grayscale-[0.4] transition-[filter] duration-700 hover:grayscale-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
              <a
                data-testid="company-map-link"
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${contact.zip} ${contact.address}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="absolute bottom-4 right-4 flex items-center gap-2 bg-ink/90 px-4 py-2 font-mono text-[10px] tracking-[0.2em] text-white backdrop-blur transition-colors hover:bg-accent hover:text-ink"
              >
                <MapPin className="h-3.5 w-3.5" />
                Googleマップで開く
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaSection />
    </div>
  );
}
